import React, { useEffect, useRef } from "react";
import { DEFAULT_LABORATORY } from "@app/pages/samples/utils/constants";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ProcedureType, UserType } from "@app/types/genericTables.types";
import {
  AnalysisCurrent,
  PreparationTypes,
  SampleStateType,
  SampleStateProcessingType,
} from "@app/types/samples.types";
import useTabs from "@app/pages/samples/utils/useTab";
import { useSample } from "@app/pages/samples/utils/useSample";
import useAuth from "@app/context/AuthContext";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { formatDateOrNow } from "@app/utils/formatDate";
import { setsAnalysisPhasesPuAmCM } from "@app/pages/samples/config/phases/AnalysisPhases";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";

const getDefaultValues = (
  currentPreparationIndex?: PreparationTypes,
  currentCounting?: AnalysisCurrent,
  currentCalculation?: AnalysisCurrent,
  sampling?: SampleStateType,
  user?: UserType,
  filteredProceduresPRE?: ProcedureType,
  filteredProceduresCON?: ProcedureType,
  filteredCalculationCAL?: ProcedureType,
  idMuestra?: string,
  processing?: SampleStateProcessingType
) => {
  const currentSampling = sampling?.current;
  const currentCountingIndex =
    currentPreparationIndex?.countings?.[currentCounting?.index as number];
  const currentCalculationIndex =
    currentCountingIndex?.calculations[currentCalculation?.index as number];

  const currentSample = useSample.getState().get(Number(idMuestra));

  const currentProcessing = useSample
    .getState()
    .getCurrentProcessing()?.current;

  const dateTimeForCategoryDigestion = currentProcessing.find(
    (item) => item.processing_category?.id === 6
  )?.date_time;

  // Obtener el array de `processing.current`
  const processingArray = processing?.current ?? [];

  // Buscar el valor de `dry_calcination` dentro de processing.current
  const wetDryValue = processingArray.find(
    (item) => item.wet_dry !== null && item.wet_dry !== undefined
  )?.wet_dry;

  const dryCalcinationValue = processingArray.find(
    (item) =>
      item.dry_calcination !== null && item.dry_calcination !== undefined
  )?.dry_calcination;

  const defaultTypeUnit = sampling?.current?.unit_types;

  return {
    //preparation
    analysis_analyst: currentPreparationIndex?.analyst || user,
    preparation_procedure:
      currentPreparationIndex?.preparation_procedure ||
      filteredProceduresPRE ||
      undefined,
    start_date: formatDateOrNow(currentPreparationIndex?.start_date),
    analysis_amount_sample: currentPreparationIndex?.amount_sample || 0,
    analysis_equipment: currentPreparationIndex?.equipment || [],
    wet_dry: wetDryValue,
    dry_calcination: dryCalcinationValue,
    equipment: currentPreparationIndex?.equipment || [],
    start_date_precipitation: currentPreparationIndex?.start_date_precipitation,
    valid_preparation: "N/A",
    analysis_unit_types: currentPreparationIndex?.unit_types || defaultTypeUnit,

    //counting
    counting_analyst: currentCountingIndex?.analyst || user,
    counting_procedure:
      currentCountingIndex?.counting_procedure || filteredProceduresCON,
    counting_equipment: currentCountingIndex?.equipment || undefined,
    counting_detector: currentCountingIndex?.detectors || undefined,
    counting_date: currentCountingIndex?.counting_date || dayjs().toISOString(),
    counting_valid: currentCountingIndex?.counting_valid || "NA",

    //calculation
    start_date_time: formatDateOrNow(currentSampling?.date_start_sampling),
    end_date_time: formatDateOrNow(currentSampling?.date_end_sampling),
    calculation_amount_sample:
      currentPreparationIndex?.amount_sample || undefined,
    calculation_units: currentPreparationIndex?.unit_types || undefined,
    calculation_shared: currentSample?.shared ? "Si" : "No",
    calculation_analyst: currentCalculationIndex?.analyst || user,
    laboratory: currentCalculationIndex?.laboratory || DEFAULT_LABORATORY,
    calculation_procedure:
      currentCalculationIndex?.calculation_procedure || filteredCalculationCAL,
    calculation_date: formatDateOrNow(
      currentCalculationIndex?.calculation_date
    ),
    //trazador: currentPreparationIndex?.analysis_type[0].isotope_type|| "",
    isotope_type: currentPreparationIndex?.analysis_type[0].isotope_type,
    isotope_code: currentPreparationIndex?.analysis_type[0].isotope_code,
    idResultPreload: currentCalculationIndex?.result?.id || undefined,
    source_app_calculation_info_file:
      currentCalculationIndex?.source_app_calculation_info_file || null,
    source_app_calculation_info_file_genie:
      currentCalculationIndex?.source_app_calculation_info_file || null,
    calculation_date_countig_file: undefined,
    calculation_equipment_file_generic: undefined,
    calculation_equipment_file: currentCalculationIndex?.calculation_equipment_file,
    calculation_detectors_generic: undefined,
    calculation_detectors: currentCalculationIndex?.calculation_detectors,
    calculation_time_count: currentCalculationIndex?.calculation_time_count,
    calculation_volume: undefined,
    calibration_source_certificate: undefined,
    calculation_background: undefined,
    calculation_calibration: undefined,
    calculation_library: undefined,
    calculation_sample: undefined,
    totalUncertainty: currentCalculationIndex?.result?.uncertainty || null,
    totalActivity: currentCalculationIndex?.result?.activity || null,
    totalLid: currentCalculationIndex?.result?.lid || null,
    efficiencieTotal: currentCalculationIndex?.result?.efficiency || null,
    trazador: null,
    analysis_start_date_time:
      dateTimeForCategoryDigestion || currentPreparationIndex?.start_date,
  };
};

const PuAmCMForm = () => {
  const { tabValueAnalysis } = useTabs();

  const form = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { sampling } = stateSample;
  const { processing } = stateSample;
  const { user } = useAuth();
  const filteredProceduresPRE = useFilteredProcedures("PRE");
  const filteredProceduresCON = useFilteredProcedures("CON");
  const filteredCalculationCAL = useFilteredProcedures("CAL");

  // Ref para almacenar valores parseados que deben persistir
  const preservedValuesRef = useRef<any>({});

  const {
    currentPreparation,
    preparationsList,
    currentCounting,
    currentCalculation,
  } = useAnalysis();

  const currentPreparationObject = preparationsList.filter((preparation) =>
    preparation.analysis_type.some(
      (analysis) => analysis.id === form.getValues("typeAnalysis")?.id
    )
  )[currentPreparation.index as number];

  useEffect(() => {
    if (currentPreparationObject) {
      const currentValues = form.getValues();

      const newValues = {
        ...currentValues,
        ...getDefaultValues(
          currentPreparationObject,
          currentCounting,
          currentCalculation,
          sampling,
          user,
          filteredProceduresPRE?.[0],
          filteredProceduresCON?.[0],
          filteredCalculationCAL?.[0],
          idMuestra,
          processing
        ),
      };

      // Preservar valores parseados del archivo si existen
      const fieldsToPreserve = [
        'totalActivity',
        'totalUncertainty', 
        'totalLid',
        'efficiencieTotal',
        'calculation_date_countig_file',
        'calculation_equipment_file_generic',
        'calculation_equipment_file',
        'calculation_detectors_generic',
        'calculation_detectors',
        'counting_detector',
        'calculation_time_count',
        'calculation_volume',
        'calibration_source_certificate',
        'calculation_background',
        'calculation_calibration',
        'calculation_library',
        'calculation_sample',
        'trazador'
      ];

      fieldsToPreserve.forEach(field => {
        if (currentValues[field] !== undefined && currentValues[field] !== null && currentValues[field] !== '') {
          newValues[field] = currentValues[field];
        }
      });

      form.reset(newValues);
    }
  }, [idMuestra, JSON.stringify(currentPreparationObject), currentCounting]);

  const CurrentComponent =
    setsAnalysisPhasesPuAmCM[tabValueAnalysis]?.component;
  return CurrentComponent ? <CurrentComponent /> : null;
};

export default PuAmCMForm;
